import { useEffect, useMemo, useState } from 'react';
import accessToken from '|/helpers/access-token';
import { getCustomerAccessories, Accessory } from '|/services/slate-accessories';

// Interim source (TXCC-9545): accessories are fetched from the slate-accessories
// BFF because the review-summary endpoint doesn't return them yet. Once BE-5 adds
// accessories to the summary response, drop this hook and read them (and their
// total) straight off `summary` like the other price-breakdown line items.
const useSlateAccessories = (enabled: boolean) => {
  const [accessories, setAccessories] = useState<Accessory[]>([]);

  useEffect(() => {
    const customerId = accessToken().user?.user_id ?? '';
    if (!enabled || !customerId) {
      setAccessories([]);
      return undefined;
    }

    const abortController = new AbortController();
    getCustomerAccessories(customerId, abortController.signal)
      .then(res => {
        if (abortController.signal.aborted) return;
        setAccessories(res?.accessories ?? []);
      })
      .catch(() => {
        // Best-effort — accessories are secondary content in the order summary.
        // A failure just hides the itemized rows; never surface it to the load boundary.
      });

    return () => abortController.abort();
  }, [enabled]);

  const accessoriesTotal = useMemo(
    () =>
      (accessories ?? []).reduce(
        (sum, accessory) =>
          sum + accessory.price + (accessory.installation?.factoryInstall?.price ?? 0),
        0
      ),
    [accessories]
  );

  return { accessories, accessoriesTotal };
};

export default useSlateAccessories;
